import { designTokens, RiCircleFill } from "@itandi/itandi-bb-ui";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import type React from "react";
import type { DealConstructionContext } from "~/models/DealConstruction";
import { isDealBillingContext } from "~/models/DealConstruction";
import { toThousandSeparator } from "~/utils/CurrencySeparator";
import Styles from "./ConstructionTop.module.scss";

type Props = {
	billingTarget?: "owner" | "rentee" | null;
	constructionContext: DealConstructionContext;
	dealEstimationHistoryContext?: DealConstructionContext | null;
	ownManagement?: boolean;
	showSubtotal: boolean;
};

export const ConstructionAmounts: React.FC<Props> = ({
	constructionContext,
	dealEstimationHistoryContext = null,
	billingTarget = null,
	ownManagement = false,
	showSubtotal
}: Props) => {
	const showSettlementInformation =
		isDealBillingContext(constructionContext) &&
		constructionContext.dealSettlements.length > 0;
	const showEstimationHistory = dealEstimationHistoryContext && !ownManagement;
	// 区分別小計の表示元: 申請履歴があればそちら、なければ確定見積
	const subtotalAmountContext = showEstimationHistory
		? dealEstimationHistoryContext
		: constructionContext;
	const totalAmountLabel = ((): string => {
		if (billingTarget) return "施工項目総額";
		if (showEstimationHistory) return "申請前総額";
		return "総額";
	})();
	return (
		<Flex direction="column" gap={designTokens["spacing/s"]}>
			{showSettlementInformation ? (
				<div>
					<span className={Styles.TotalAmount}>
						精算項目総額：¥
						{toThousandSeparator(
							billingTarget === "owner"
								? constructionContext.settlementOwnerTotalAmountWithTax
								: constructionContext.settlementRenteeTotalAmountWithTax
						)}
					</span>
					<span className={Styles.TotalAmountWithoutTax}>
						税抜：¥
						{toThousandSeparator(
							billingTarget === "owner"
								? constructionContext.settlementOwnerTotalAmount
								: constructionContext.settlementRenteeTotalAmount
						)}
					</span>
				</div>
			) : null}
			<Flex direction="row" gap={designTokens["spacing/m"]}>
				<Flex direction="column" gap={designTokens["spacing/xxs"]}>
					{showEstimationHistory ? (
						<div>
							<span className={Styles.TotalAmount}>
								申請後総額：¥
								{toThousandSeparator(
									dealEstimationHistoryContext.totalAmountWithTax
								)}
							</span>
							<span className={Styles.TotalAmountWithoutTax}>
								税抜：¥
								{toThousandSeparator(dealEstimationHistoryContext.totalAmount)}
							</span>
						</div>
					) : null}
					<div>
						<span className={Styles.TotalAmount}>
							{totalAmountLabel}：¥
							{toThousandSeparator(constructionContext.totalAmountWithTax)}
						</span>
						<span className={Styles.TotalAmountWithoutTax}>
							税抜：¥
							{toThousandSeparator(constructionContext.totalAmount)}
						</span>
					</div>
				</Flex>
				{showSubtotal ? (
					<>
						<div
							className={Styles.SubtotalDivider}
							css={{
								borderLeft: `1px solid ${designTokens["color/border/default"]}`
							}}
						/>
						<Flex direction="column" gap={designTokens["spacing/xs"]}>
							<Flex alignItems="center" gap={designTokens["spacing/xxs"]}>
								<RiCircleFill
									color={
										designTokens["color/chips/presentation/foreground/red"]
									}
									size="0.75rem"
								/>
								<span className={Styles.SubtotalLabel}>
									{showEstimationHistory
										? "申請後の提案項目を除く小計："
										: "提案項目を除く小計："}
								</span>
								<span className={Styles.SubtotalAmount}>
									¥
									{toThousandSeparator(
										subtotalAmountContext.nonProposalConstructionTotalAmount
									)}
								</span>
								<span className={Styles.SubtotalTaxNote}>（税抜）</span>
							</Flex>
							<Flex alignItems="center" gap={designTokens["spacing/xxs"]}>
								<RiCircleFill
									color={
										designTokens["color/chips/presentation/foreground/blue"]
									}
									size="0.75rem"
								/>
								<span className={Styles.SubtotalLabel}>
									{showEstimationHistory
										? "申請後の提案項目小計："
										: "提案項目小計："}
								</span>
								<span className={Styles.SubtotalAmount}>
									¥
									{toThousandSeparator(
										subtotalAmountContext.proposalConstructionTotalAmount
									)}
								</span>
								<span className={Styles.SubtotalTaxNote}>（税抜）</span>
							</Flex>
						</Flex>
					</>
				) : null}
			</Flex>
		</Flex>
	);
};
